//====== WR data 15/09/2026 — SmartFlow Item Importer (Fase 3)
// Motivo da alteração: modo "Item" do SmartFlow Importer — mesmo padrão de UI de
// SmartFlowImporterForm.js (Parceiro), mas mais enxuto: sem "Validação Receita" (não existe fonte
// oficial equivalente pra item) e sem endereço/fiscal na gravação. 2 abas:
//  - "Itens identificados": lê AMBAS as pastas do Drive do projeto (Fornecedores + Clientes) e
//    CONSOLIDA os itens numa lista única, deduplicados por EAN (ou NCM+descrição sem EAN) —
//    decisão do usuário: o mesmo Item pode vir de uma nota de compra ou de venda, mas alimenta o
//    mesmo cadastro de Materiais do SAP.
//  - "Revisão e Gravação SAP": preenchida por "Sugerir com IA" (SmartFlowItemAiService, Fase 2) —
//    todos os campos ficam editáveis, o usuário confirma antes de gravar. Linha amarela quando o
//    item já existe no SAP (mesmo padrão de Parceiro): vai virar ATUALIZAÇÃO, não criação.
//======
import React, { useMemo, useState } from "react"
import {
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Input,
  Button,
  Badge,
  Card,
  CardBody,
  Row,
  Col,
} from "reactstrap"
import classnames from "classnames"
import { utils, writeFile } from "xlsx"

import { listDriveFilesComProgresso } from "services/smartflow/importerApi"
import {
  sugerirItensComIA,
  salvarItensStaging,
  buscarItensGravadosNoSapPorEan,
  incrementarProximoNumeroItem,
  lerSequenciaItemDoProjeto,
} from "services/smartflow/itemImporterApi"
import {
  buscarGruposItemSap,
  buscarUnidadesMedidaSap,
  buscarItemCodesPorEans,
  buscarNcmsValidos,
  gravarItemNoSap,
} from "services/smartflow/itemSapWrite"
import {
  formatQuantidade,
  formatValorItem,
  formatConfianca,
  chaveDedupItem,
  montarItemSap,
  validarItemSap,
} from "components/SmartFlow/itemImporterLogic"

function ConfiancaBadge({ confianca }) {
  if (!confianca) return <Badge color="secondary">—</Badge>
  const cor = { alta: "success", media: "warning", baixa: "secondary" }[confianca] || "secondary"
  return <Badge color={cor}>{formatConfianca(confianca)}</Badge>
}

//====== WR data 16/09/2026 — pedido do usuário: Tipo de material na grade. Mesmos 12 códigos do
// enum SAPB1.BoMaterialTypes confirmados no $metadata da Service Layer (SmartFlowItemAiService.js
// tem a mesma lista, do lado do backend) — "Serviços" cobre o rádio Serviço/Material da tela do SAP.
const MATERIAL_TYPES = [
  { value: "mt_GoodsForReseller", label: "Mercadoria para Revenda" },
  { value: "mt_RawMaterial", label: "Matéria-prima" },
  { value: "mt_Package", label: "Embalagem" },
  { value: "mt_GoodsInProcess", label: "Mercadorias em processo" },
  { value: "mt_FinishedGoods", label: "Produtos acabados" },
  { value: "mt_SubProduct", label: "Subproduto" },
  { value: "mt_IntermediateMaterial", label: "Produto Intermediário" },
  { value: "mt_ConsumerMaterial", label: "Material de Uso e Consumo" },
  { value: "mt_FixedAsset", label: "Ativo Imobilizado" },
  { value: "mt_Service", label: "Serviços" },
  { value: "mt_OtherInput", label: "Outros insumos" },
  { value: "mt_Other", label: "Outras" },
]

// Origem da mercadoria (tag <orig> da NF-e, ICMS) — só exibição, vem exato do XML.
const ORIGEM_ICMS_LABELS = {
  0: "0 - Nacional",
  1: "1 - Estrangeira (importação direta)",
  2: "2 - Estrangeira (mercado interno)",
  3: "3 - Nacional, Cont. Importação >40%",
  4: "4 - Nacional, PPB",
  5: "5 - Nacional, Cont. Importação ≤40%",
  6: "6 - Estrangeira, sem similar nacional",
  7: "7 - Estrangeira, mercado interno, sem similar",
  8: "8 - Nacional, Cont. Importação >70%",
}

export default function SmartFlowItemImporterForm({ project, userEmail, onNotify }) {
  const projectId = project?._id
  const serviceLayerUrl = project?.serviceLayerUrl || ""
  const importerCfg = project?.smartflowImporter || {}
  const itemCfg = importerCfg.item || {}

  const notify = (msg, type = "info") => {
    if (onNotify) onNotify(msg, type)
    else console.log(`[SmartFlow][${type}] ${msg}`)
  }

  const pastasConectadas = ["fornecedor", "cliente"].filter((t) => importerCfg?.[t]?.driveFolderId)

  const [openTab, setOpenTab] = useState("itens")

  const [itens, setItens] = useState(null) // lista consolidada, deduplicada
  const [listLoading, setListLoading] = useState(false)
  //====== WR data 16/09/2026 — pedido do usuário: "Listar arquivos" (lê e extrai TODOS os XMLs
  // das pastas Fornecedores+Clientes, pode ter centenas de notas) ficava só com "Listando...",
  // sem noção nenhuma de quanto falta. Não dá pra saber o progresso DENTRO de uma pasta (o
  // backend lê tudo numa chamada só), mas dá pra mostrar quantas das pastas já voltaram.
  const [progressoListagem, setProgressoListagem] = useState(null)
  // WR data 16/09/2026 — progresso por XML lido (soma das 2 pastas), pedido do usuário: pasta
  // grande dava só o total de pastas (0/1/2), muito grosseiro pra centenas de notas.
  const [progressoXml, setProgressoXml] = useState(null)
  const [filtroItens, setFiltroItens] = useState("")
  const [selectedIds, setSelectedIds] = useState(() => new Set())

  const [importRows, setImportRows] = useState(null)
  const [selectedImportIds, setSelectedImportIds] = useState(() => new Set())
  const [sugerindo, setSugerindo] = useState(false)
  //====== WR data 15/09/2026 — pedido do usuário: "Preparar dados" demora (a IA roda em blocos
  // sequenciais de 15 itens) e ficava sem nenhum retorno visual até terminar tudo.
  const [progressoPreparo, setProgressoPreparo] = useState(null)
  const [etapaPreparo, setEtapaPreparo] = useState(null)
  const [gravando, setGravando] = useState(false)
  const [resultadosGravacao, setResultadosGravacao] = useState({})
  const [progresso, setProgresso] = useState(null)
  //====== WR data 15/09/2026 — guardados pra alimentar os selects de Grupo/Unidade na grade de
  // revisão (antes só eram usados de passagem dentro de handlePrepararComIA).
  const [gruposSap, setGruposSap] = useState([])
  const [unidadesSap, setUnidadesSap] = useState([])
  //====== WR data 01/10/2026 — Motivo da alteração: bug grave reportado ao vivo — preparar um 2º
  // lote de itens na MESMA sessão, depois de já ter gravado um 1º lote no SAP, gerava os MESMOS
  // ItemCodes do 1º lote (ex.: IT000018 de novo). Causa: `project` é uma prop recebida uma vez no
  // mount — o número gravado no Mongo avança de verdade (incrementarProximoNumeroItem), mas o
  // `project` em memória nunca é recarregado, então a PRÓXIMA chamada de "Preparar dados" na
  // mesma sessão lia de novo o valor ANTIGO de `project.smartflowImporter.item.itProximoNumero`.
  // Como o ItemCode colidente já existia de verdade no SAP (tinha acabado de ser criado), o
  // `gravarItemNoSap` caía no caminho de "já existe" e fazia um PATCH PARCIAL — só com os campos
  // do item novo, deixando os campos que o item novo não tinha (ex.: NCM) com o valor do item
  // ANTIGO que ficou por baixo, intocado. Resultado: item corrompido com uma mistura dos dois.
  // Este estado local é quanto já foi CONSUMIDO nesta sessão, somado ao número lido de `project`
  // (baseline, só a 1ª vez) pra sempre saber o próximo número de verdade, mesmo sem recarregar
  // `project` entre uma gravação e a próxima preparação.
  const [itemsConsumidosNaSessao, setItemsConsumidosNaSessao] = useState(0)

  //====== Listar — 1x por pasta conectada (as duas juntas), consolida por chaveDedupItem: o
  // mesmo item pode aparecer em nota de compra E de venda, mas entra só 1 vez na lista.
  const handleListar = async () => {
    if (pastasConectadas.length === 0) {
      notify(
        "Nenhuma pasta do Drive configurada. Configure em Alterando Projeto > SmartFlow Importer.",
        "warning"
      )
      return
    }
    setListLoading(true)
    setProgressoListagem({ concluidas: 0, total: pastasConectadas.length })
    setProgressoXml(null)
    try {
      let concluidas = 0
      const progressoXmlPorPasta = {}
      const atualizarProgressoXml = () => {
        const valores = Object.values(progressoXmlPorPasta)
        setProgressoXml({
          concluidos: valores.reduce((soma, p) => soma + p.concluidos, 0),
          total: valores.reduce((soma, p) => soma + p.total, 0),
        })
      }

      const resultados = await Promise.all(
        pastasConectadas.map((tipo) =>
          listDriveFilesComProgresso(importerCfg[tipo].driveFolderId, tipo, (concluidosXml, totalXml) => {
            progressoXmlPorPasta[tipo] = { concluidos: concluidosXml, total: totalXml }
            atualizarProgressoXml()
          }).then((r) => {
            concluidas += 1
            setProgressoListagem({ concluidas, total: pastasConectadas.length })
            return { tipo, ...r }
          })
        )
      )

      const porChave = new Map()
      resultados.forEach(({ tipo, notas }) => {
        ;(notas || []).forEach((nota) => {
          ;(nota.itens || []).forEach((item) => {
            const base = {
              ...item,
              fileId: nota.fileId,
              fileName: nota.fileName,
              numeroNF: nota.numeroNF,
              origemTipo: tipo,
              //====== WR data 15/09/2026 — pedido do usuário: mostrar de qual Fornecedor/Cliente
              // veio o item, não só o nome do arquivo/número da nota. Pasta Fornecedores -> emit
              // (quem vendeu o item pra gente); pasta Clientes -> dest (quem comprou) — já vem
              // resolvido por extractNFeData conforme o `tipo` da pasta lida.
              origemParceiroNome: nota.nomeFantasia || nota.razaoSocial || null,
            }
            const chave = chaveDedupItem(base)
            if (!porChave.has(chave)) {
              porChave.set(chave, { ...base, chave, ocorrencias: 1 })
            } else {
              porChave.get(chave).ocorrencias += 1
            }
          })
        })
      })

      const lista = [...porChave.values()].sort((a, b) => (a.xProd || "").localeCompare(b.xProd || ""))
      setItens(lista)
      setSelectedIds(new Set(lista.map((i) => i.chave)))
      notify(`${lista.length} item(ns) identificado(s) nas notas lidas.`, "success")
    } catch (error) {
      notify(error?.message || "Falha ao listar os arquivos do Drive.", "danger")
    } finally {
      setListLoading(false)
      setProgressoListagem(null)
      setProgressoXml(null)
    }
  }

  const itensFiltrados = useMemo(() => {
    if (!itens) return []
    const termo = filtroItens.trim().toLowerCase()
    if (!termo) return itens
    return itens.filter((i) =>
      [i.xProd, i.cProd, i.cEAN, i.ncm, i.origemParceiroNome]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(termo))
    )
  }, [itens, filtroItens])

  const toggleSelecionado = (chave) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(chave)) next.delete(chave)
      else next.add(chave)
      return next
    })
  }

  const todosSelecionados = itensFiltrados.length > 0 && itensFiltrados.every((i) => selectedIds.has(i.chave))
  const toggleSelecionarTodos = () => {
    setSelectedIds(() => (todosSelecionados ? new Set() : new Set(itensFiltrados.map((i) => i.chave))))
  }

  //====== Preparar dados (com IA) — pré-checa duplicidade no SAP por EAN (mesmo papel do CNPJ pro
  // lado de Parceiro), monta a sequência de ItemCode e chama a sugestão em lote.
  const handlePrepararComIA = async () => {
    const selecionados = itensFiltrados.filter((i) => selectedIds.has(i.chave))
    if (selecionados.length === 0) {
      notify("Marque pelo menos um item antes de preparar a importação.", "warning")
      return
    }

    setSugerindo(true)
    setProgressoPreparo(null)
    //====== WR data 15/09/2026
    // Motivo: reportado ao vivo — a barra demorava pra aparecer mesmo depois de paralelizar a
    // IA. Causa: as etapas ANTES da IA (checar EAN já existente + ler Grupos/Unidades do SAP)
    // não mostravam nada — "Preparando..." ficava mudo até a 1ª sugestão da IA chegar, mesmo que
    // essas etapas (Service Layer real) já estivessem levando vários segundos sozinhas. Etapa
    // textual pra sempre ter algo visível, e as duas checagens de EAN paralelizadas (eram
    // sequenciais, uma esperando a outra à toa).
    // ======
    setEtapaPreparo("Verificando itens já existentes no SAP...")
    try {
      let gravadosNoSap = {}
      const eans = selecionados.map((i) => i.cEAN).filter(Boolean)
      if (serviceLayerUrl && eans.length) {
        try {
          const [porMongo, porSap] = await Promise.all([
            buscarItensGravadosNoSapPorEan(projectId, eans),
            buscarItemCodesPorEans(serviceLayerUrl, eans),
          ])
          gravadosNoSap = { ...porMongo, ...porSap }
        } catch (error) {
          console.error("[SmartFlow] Falha ao checar itens já existentes:", error)
        }
      }

      setEtapaPreparo("Carregando Grupos, Unidades e NCM do SAP...")
      let grupos = []
      let unidades = []
      let ncmsValidos = {}
      if (serviceLayerUrl) {
        try {
          const ncms = selecionados.map((i) => i.ncm).filter(Boolean)
          ;[grupos, unidades, ncmsValidos] = await Promise.all([
            buscarGruposItemSap(serviceLayerUrl),
            buscarUnidadesMedidaSap(serviceLayerUrl),
            buscarNcmsValidos(serviceLayerUrl, ncms),
          ])
        } catch (error) {
          console.error("[SmartFlow] Falha ao ler Grupos/Unidades/NCM do SAP:", error)
        }
      }
      setGruposSap(grupos)
      setUnidadesSap(unidades)

      setEtapaPreparo(itemCfg.usarSugestaoIA ? "Sugerindo com IA..." : null)
      const sugestoes = itemCfg.usarSugestaoIA
        ? await sugerirItensComIA(
            projectId,
            selecionados,
            grupos.map((g) => ({ Code: g.code, Name: g.name })),
            unidades.map((u) => ({ Code: u.code, Name: u.name })),
            (itensProcessados, totalItens, loteAtual, totalLotes) =>
              setProgressoPreparo({ itensProcessados, totalItens, atual: loteAtual, total: totalLotes })
          )
        : []
      //====== WR data 15/09/2026
      // Motivo: casar a sugestão pela POSIÇÃO no array (idx) está errado — o backend agrupa
      // primeiro os itens com match exato de EAN (resolvidos sem IA) e só depois os que passaram
      // pela IA, então a ORDEM da resposta não é a mesma de `selecionados`. O id sintético que o
      // backend devolve (reindexado em itemImporterApi.sugerirItensComIA pra já vir na escala
      // global do lote inteiro) é a única referência confiável.
      // ======
      const sugestaoPorIndiceGlobal = new Map(sugestoes.map((s) => [s.id, s]))

      const seq = lerSequenciaItemDoProjeto(project)
      // `project` só reflete o número no momento do mount — soma o que já foi consumido NESTA
      // sessão (ver declaração de itemsConsumidosNaSessao) pra nunca reusar código de um lote já
      // gravado antes, mesmo sem recarregar `project`.
      let contador = (seq.proximoNumero || 1) - 1 + itemsConsumidosNaSessao

      const rows = selecionados.map((item, idx) => {
        const sugestaoIA = sugestaoPorIndiceGlobal.get(idx + 1)
        // Grupo do Item: usa a sugestão da IA; sem ela (ou "sem IA" desligado), cai no Grupo
        // padrão configurado no projeto — sempre editável na grade de qualquer forma.
        const sugestao =
          sugestaoIA || itemCfg.grupoCode
            ? { ...(sugestaoIA || {}), itemsGroupCode: sugestaoIA?.itemsGroupCode ?? itemCfg.grupoCode ?? null }
            : null

        const codigoExistente = item.cEAN ? gravadosNoSap[item.cEAN] : null
        //====== WR data 15/09/2026
        // Motivo: o ItemCode NUNCA deve vir da IA — decisão confirmada no plano (seção 3.3): a
        // geração é por sequência, igual ao CardCode do Parceiro. Deixar a IA sugerir o próprio
        // ItemCode fazia todo item de um lote grande sair com o MESMO código (a IA não garante
        // unicidade entre itens de uma mesma resposta), o que quebrava a seleção da grade inteira
        // (a chave de cada linha usa o ItemCode).
        let itemCode = codigoExistente || null
        const usouSequencia = !itemCode && !!seq.usaPrefixo
        if (usouSequencia) {
          contador += 1
          itemCode = `${seq.prefixo}${String(contador).padStart(seq.qtdCaracteresSequencia, "0")}`
        }
        // buscarNcmsValidos devolve { [ncmDigits]: absEntry } (WR 16/09/2026 v2) — o AbsEntry, não
        // um booleano, é o que precisa ir até montarPayloadItem (itemSapWrite.js) pra gravar o NCM.
        return montarItemSap(item, itemCode, sugestao, !!codigoExistente, usouSequencia, ncmsValidos[item.ncm])
      })

      setImportRows(rows)
      setSelectedImportIds(new Set(rows.map((r) => r.chave)))
      setResultadosGravacao({})
      setOpenTab("revisao")

      const jaExistem = rows.filter((r) => r.jaGravadoNoSap).length
      if (jaExistem > 0) {
        notify(
          `${jaExistem} item(ns) já existe(m) no SAP (linha amarela) — serão ATUALIZADOS, não criados.`,
          "warning"
        )
      }
    } catch (error) {
      notify(error?.message || "Falha ao preparar os itens.", "danger")
    } finally {
      setSugerindo(false)
      setProgressoPreparo(null)
      setEtapaPreparo(null)
    }
  }

  // Identificador ESTÁVEL da linha (não muda se o usuário editar o ItemCode) — ver comentário em
  // itemImporterLogic.montarItemSap.
  const rowKey = (row) => row.chave

  const updateImportField = (row, field, value) => {
    const key = rowKey(row)
    setImportRows((prev) =>
      prev.map((r) => {
        if (rowKey(r) !== key) return r
        const atualizado = { ...r, [field]: value }
        // WR data 16/09/2026 — trocar o Tipo de material recalcula "Sujeito a retenção de
        // imposto" junto (pedido do usuário: nunca marcado pra item que não é Serviço).
        if (field === "MaterialType") {
          atualizado.WTLiable = value === "mt_Service" ? "tYES" : "tNO"
        }
        return atualizado
      })
    )
  }

  const validacoesImport = useMemo(() => {
    const contagemItemCode = new Map()
    ;(importRows || []).forEach((row) => {
      const code = (row.ItemCode || "").trim()
      if (!code) return
      contagemItemCode.set(code, (contagemItemCode.get(code) || 0) + 1)
    })
    const mapa = new Map()
    ;(importRows || []).forEach((row) => {
      mapa.set(rowKey(row), validarItemSap(row, contagemItemCode))
    })
    return mapa
  }, [importRows])

  const importSelecionaveis = (importRows || []).filter((r) => validacoesImport.get(rowKey(r))?.ok)

  const toggleImportFlag = (row) => {
    const key = rowKey(row)
    if (!validacoesImport.get(key)?.ok) return
    setSelectedImportIds((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const todosImportSelecionados =
    importSelecionaveis.length > 0 &&
    importSelecionaveis.every((r) => selectedImportIds.has(rowKey(r)))
  const toggleSelecionarTodosImport = () => {
    setSelectedImportIds(() =>
      todosImportSelecionados ? new Set() : new Set(importSelecionaveis.map((r) => rowKey(r)))
    )
  }

  //====== Gravar SAP — staging (backend) + POST/PATCH Items (Service Layer do projeto).
  const handleGravarStaging = async () => {
    const selecionados = (importRows || []).filter(
      (r) => selectedImportIds.has(rowKey(r)) && validacoesImport.get(rowKey(r))?.ok
    )
    if (selecionados.length === 0) {
      notify("Marque pelo menos um item válido antes de gravar.", "warning")
      return
    }
    if (!serviceLayerUrl) {
      notify("O projeto não tem a URL da Service Layer configurada.", "danger")
      return
    }

    setGravando(true)
    setProgresso({ atual: 0, total: selecionados.length, etapa: "Salvando no banco..." })

    const usouSequenciaPorItemCode = new Map(selecionados.map((r) => [r.ItemCode, !!r.usouSequencia]))
    const chavePorItemCode = new Map(selecionados.map((r) => [r.ItemCode, rowKey(r)]))

    try {
      const salvos = await salvarItensStaging(projectId, selecionados, userEmail || null)

      let criados = 0
      let atualizados = 0
      let numerosConsumidos = 0
      const erros = []
      const novosResultados = {}

      let indice = 0
      for (const item of salvos || []) {
        indice += 1
        setProgresso({
          atual: indice,
          total: salvos.length,
          etapa: `Gravando no SAP: ${item.itemName || item.itemCode}...`,
        })
        const key = chavePorItemCode.get(item.itemCode)
        try {
          const resultado = await gravarItemNoSap(
            item,
            userEmail || null,
            serviceLayerUrl,
            usouSequenciaPorItemCode.get(item.itemCode)
          )
          if (resultado.acaoNoSap === "atualizado") {
            atualizados += 1
          } else {
            criados += 1
            if (usouSequenciaPorItemCode.get(item.itemCode)) numerosConsumidos += 1
          }
          if (key) novosResultados[key] = { status: "sucesso" }
        } catch (error) {
          const mensagem = error?.message || "falha desconhecida"
          erros.push(`${item.itemCode}: ${mensagem}`)
          if (key) novosResultados[key] = { status: "erro", mensagem }
        }
      }
      setResultadosGravacao((prev) => ({ ...prev, ...novosResultados }))

      if (numerosConsumidos > 0) {
        incrementarProximoNumeroItem(projectId, numerosConsumidos)
        // local primeiro (síncrono) — a próxima "Preparar dados" desta sessão já precisa saber
        // disso, sem depender de `project` ser recarregado (ver declaração do state).
        setItemsConsumidosNaSessao((prev) => prev + numerosConsumidos)
      }

      const sucesso = criados + atualizados
      const resumo = [
        criados > 0 ? `${criados} criado${criados === 1 ? "" : "s"}` : null,
        atualizados > 0 ? `${atualizados} atualizado${atualizados === 1 ? "" : "s"}` : null,
      ]
        .filter(Boolean)
        .join(", ")

      if (erros.length === 0) {
        notify(`${resumo} no SAP.`, "success")
      } else if (sucesso === 0) {
        notify(`Falha ao gravar no SAP — ${erros.join(" | ")}`, "danger")
      } else {
        notify(`${resumo} no SAP. Falhas: ${erros.join(" | ")}`, "warning")
      }
    } catch (error) {
      notify(error?.message || "Falha ao gravar os itens.", "danger")
    } finally {
      setGravando(false)
      setProgresso(null)
    }
  }

  const handleExportarExcel = () => {
    if (!importRows?.length) return
    const linhas = importRows.map((row) => {
      const validacao = validacoesImport.get(rowKey(row)) || { ok: true, erros: [] }
      return {
        "Nota de origem": `${row.numeroNF || "—"} — ${row.fileName}`,
        ItemCode: row.ItemCode,
        "ItemName (descrição)": row.ItemName,
        "Grupo (code)": row.ItemsGroupCode,
        Unidade: row.InventoryUOM,
        Estoque: row.InventoryItem,
        Venda: row.SalesItem,
        Compra: row.PurchaseItem,
        "Código de barras (EAN)": row.cEAN,
        NCM: row.ncm,
        "NCM válido no SAP": row.ncm ? (row.ncmValido ? "Sim" : "Não") : "—",
        "Tipo de material": MATERIAL_TYPES.find((mt) => mt.value === row.MaterialType)?.label || "—",
        CEST: row.CestCode || "—",
        "Origem (ICMS)": row.origemIcms != null && row.origemIcms !== "" ? ORIGEM_ICMS_LABELS[row.origemIcms] || row.origemIcms : "—",
        "Administrar por": { nenhum: "Nenhum", serie: "Nº de série", lote: "Lote" }[row.AdministrarPor] || "Nenhum",
        "Sujeito a retenção": row.WTLiable === "tYES" ? "Sim" : "Não",
        Confiança: formatConfianca(row.confiancaIA),
        "Orientação da IA": row.observacaoIA || "—",
        "Status SAP": resultadosGravacao[rowKey(row)]?.status || "—",
        "Detalhe da inconsistência": validacao.erros.join(" | "),
      }
    })
    const planilha = utils.json_to_sheet(linhas)
    const livro = utils.book_new()
    utils.book_append_sheet(livro, planilha, "Itens SAP")
    writeFile(livro, `smartflow-itens-${projectId}.xlsx`)
  }

  return (
    <Card>
      <CardBody>
        <Row className="align-items-center mb-3">
          <Col>
            <h5 className="h3 mb-0">SmartFlow Item Importer</h5>
            <small className="text-muted">
              Pastas conectadas:{" "}
              {pastasConectadas.length
                ? pastasConectadas.map((t) => (t === "cliente" ? "Clientes" : "Fornecedores")).join(" + ")
                : "nenhuma"}
            </small>
          </Col>
          <Col className="text-right">
            <Button color="info" size="sm" onClick={handleListar} disabled={listLoading}>
              {listLoading ? "Listando..." : "Listar arquivos"}
            </Button>
            {progressoListagem && (
              <div className="small text-muted mt-1">
                {progressoXml?.total
                  ? `Lendo XMLs — ${progressoXml.concluidos} de ${progressoXml.total} lidos (${progressoListagem.concluidas}/${progressoListagem.total} pastas)`
                  : `Lendo pastas do Drive — ${progressoListagem.concluidas} de ${progressoListagem.total} ${
                      progressoListagem.total === 1 ? "pasta lida" : "pastas lidas"
                    }`}
                <div className="progress mt-1" style={{ height: 4 }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${Math.max(
                        progressoXml?.total
                          ? (progressoXml.concluidos / progressoXml.total) * 100
                          : (progressoListagem.concluidas / progressoListagem.total) * 100,
                        10
                      )}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </Col>
        </Row>

        <Nav tabs>
          <NavItem>
            <NavLink
              className={classnames({ active: openTab === "itens" })}
              onClick={() => setOpenTab("itens")}
              style={{ cursor: "pointer" }}
            >
              Itens identificados {itens ? `(${itens.length})` : ""}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: openTab === "revisao" })}
              onClick={() => setOpenTab("revisao")}
              style={{ cursor: "pointer" }}
            >
              Revisão e Gravação SAP {importRows ? `(${importRows.length})` : ""}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={openTab} className="pt-3">
          <TabPane tabId="itens">
            {!itens ? (
              <p className="text-muted small">
                Clique em "Listar arquivos" para ler os XMLs das pastas configuradas.
              </p>
            ) : (
              <>
                <Row className="align-items-center mb-2">
                  <Col md="6">
                    <Input
                      type="text"
                      bsSize="sm"
                      placeholder="Buscar (descrição, código, EAN, NCM)"
                      value={filtroItens}
                      onChange={(e) => setFiltroItens(e.target.value)}
                    />
                  </Col>
                  <Col md="6" className="text-right">
                    <Button color="primary" size="sm" onClick={handlePrepararComIA} disabled={sugerindo}>
                      {sugerindo
                        ? "Preparando..."
                        : itemCfg.usarSugestaoIA
                        ? "Preparar dados (Sugerir com IA)"
                        : "Preparar dados"}
                    </Button>
                    {sugerindo && (
                      <div className="small text-muted mt-1">
                        {progressoPreparo
                          ? `Sugerindo com IA — ${progressoPreparo.itensProcessados} de ${progressoPreparo.totalItens} itens processados (lote ${progressoPreparo.atual}/${progressoPreparo.total})`
                          : etapaPreparo || "Preparando..."}
                        <div className="progress mt-1" style={{ height: 4 }}>
                          <div
                            className="progress-bar"
                            role="progressbar"
                            style={
                              progressoPreparo
                                ? { width: `${(progressoPreparo.itensProcessados / progressoPreparo.totalItens) * 100}%` }
                                : { width: "20%" }
                            }
                          />
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>
                <div className="table-responsive">
                  <Table className="align-items-center table-flush" size="sm" responsive hover>
                    <thead className="thead-light">
                      <tr>
                        <th>
                          <input type="checkbox" checked={todosSelecionados} onChange={toggleSelecionarTodos} />
                        </th>
                        <th>Descrição (XML)</th>
                        <th>Fornecedor/Cliente</th>
                        <th>Código fornecedor</th>
                        <th>EAN</th>
                        <th>NCM</th>
                        <th>Unidade</th>
                        <th>Qtd</th>
                        <th>Valor unit.</th>
                        <th>Ocorrências</th>
                        <th>Nota de origem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itensFiltrados.map((item) => (
                        <tr key={item.chave}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(item.chave)}
                              onChange={() => toggleSelecionado(item.chave)}
                            />
                          </td>
                          <td>{item.xProd || "—"}</td>
                          <td>
                            {item.origemParceiroNome || "—"}
                            {item.ocorrencias > 1 && (
                              <span className="text-muted small"> (+{item.ocorrencias - 1})</span>
                            )}
                          </td>
                          <td>{item.cProd || "—"}</td>
                          <td>{item.cEAN || "—"}</td>
                          <td>{item.ncm || "—"}</td>
                          <td>{item.unidadeComercial || "—"}</td>
                          <td>{formatQuantidade(item.quantidadeComercial)}</td>
                          <td>{formatValorItem(item.valorUnitarioComercial)}</td>
                          <td>
                            {item.ocorrencias > 1 ? (
                              <Badge color="info">{item.ocorrencias}x</Badge>
                            ) : (
                              1
                            )}
                          </td>
                          <td className="small">
                            {[item.numeroNF, item.fileName].filter(Boolean).join(" — ")}
                          </td>
                        </tr>
                      ))}
                      {itensFiltrados.length === 0 && (
                        <tr>
                          <td colSpan={10} className="text-center text-muted">
                            Nenhum item encontrado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
          </TabPane>

          <TabPane tabId="revisao">
            {!importRows ? (
              <p className="text-muted small">
                Selecione os itens na aba "Itens identificados" e clique em "Preparar dados".
              </p>
            ) : (
              <>
                <Row className="align-items-center mb-2">
                  <Col>
                    <Button
                      color="success"
                      size="sm"
                      onClick={handleGravarStaging}
                      disabled={gravando || importSelecionaveis.length === 0}
                    >
                      {gravando ? "Gravando..." : "Gravar SAP"}
                    </Button>{" "}
                    <Button color="secondary" size="sm" onClick={handleExportarExcel}>
                      Gerar Excel
                    </Button>
                  </Col>
                  {progresso && (
                    <Col className="text-right small text-muted">
                      {progresso.etapa} ({progresso.atual}/{progresso.total})
                    </Col>
                  )}
                </Row>
                <div className="table-responsive">
                  <Table className="align-items-center table-flush" size="sm" responsive hover>
                    <thead className="thead-light">
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={todosImportSelecionados}
                            onChange={toggleSelecionarTodosImport}
                          />
                        </th>
                        <th>Confiança IA</th>
                        <th>Orientação da IA</th>
                        <th>ItemCode</th>
                        <th>ItemName</th>
                        <th>Grupo</th>
                        <th>Unidade</th>
                        <th>Tipo de material</th>
                        <th>NCM</th>
                        <th>CEST</th>
                        <th>Origem</th>
                        <th>Administrar por</th>
                        <th>Retenção imp.</th>
                        <th>Estoque</th>
                        <th>Venda</th>
                        <th>Compra</th>
                        <th>EAN</th>
                        <th>Status SAP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importRows.map((row) => {
                        const key = rowKey(row)
                        const validacao = validacoesImport.get(key) || { ok: true, erros: [] }
                        const resultado = resultadosGravacao[key]
                        //====== WR data 15/09/2026 — pedido do usuário: possível duplicidade
                        // sugerida pela IA (fuzzy — NCM+descrição parecida, diferente do EAN exato
                        // que já vira jaGravadoNoSap sozinho) precisa de cor própria E de um jeito
                        // do usuário decidir se é o MESMO item (vira atualização de
                        // possivelDuplicidadeItemCode) ou um item novo mesmo (mantém a sequência).
                        const aceitouDuplicidade =
                          row.possivelDuplicidadeItemCode && row.ItemCode === row.possivelDuplicidadeItemCode
                        const classeLinha = row.jaGravadoNoSap || aceitouDuplicidade
                          ? "table-warning"
                          : row.possivelDuplicidadeItemCode
                          ? "table-info"
                          : undefined
                        return (
                          <tr key={key} className={classeLinha}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedImportIds.has(key)}
                                disabled={!validacao.ok}
                                onChange={() => toggleImportFlag(row)}
                              />
                            </td>
                            <td>
                              <ConfiancaBadge confianca={row.confiancaIA} />
                              {row.possivelDuplicidadeItemCode && !row.jaGravadoNoSap && (
                                <div className="small mt-1">
                                  {aceitouDuplicidade ? (
                                    <>
                                      <div className="text-muted">
                                        Vai ATUALIZAR {row.possivelDuplicidadeItemCode}
                                      </div>
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="p-0"
                                        onClick={() =>
                                          updateImportField(row, "ItemCode", row.itemCodeOriginal)
                                        }
                                      >
                                        manter como novo item
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <div className="text-muted">
                                        Possível duplicidade: {row.possivelDuplicidadeItemCode}
                                      </div>
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="p-0"
                                        onClick={() =>
                                          updateImportField(
                                            row,
                                            "ItemCode",
                                            row.possivelDuplicidadeItemCode
                                          )
                                        }
                                      >
                                        usar este código (atualizar)
                                      </Button>
                                    </>
                                  )}
                                </div>
                              )}
                            </td>
                            {/*====== WR data 15/09/2026 — pedido do usuário: mostrar o TEXTO da
                            orientação da IA (observacaoIA), não só o selo de confiança — sem
                            isso, não dá pra entender POR QUE ela sugeriu aquilo. */}
                            <td className="small" style={{ minWidth: 220, whiteSpace: "normal" }}>
                              {row.observacaoIA || <span className="text-muted">—</span>}
                            </td>
                            <td>
                              <Input
                                bsSize="sm"
                                style={{ minWidth: 110 }}
                                value={row.ItemCode}
                                onChange={(e) => updateImportField(row, "ItemCode", e.target.value)}
                              />
                            </td>
                            <td>
                              <Input
                                bsSize="sm"
                                style={{ minWidth: 260 }}
                                value={row.ItemName}
                                onChange={(e) => updateImportField(row, "ItemName", e.target.value)}
                              />
                            </td>
                            <td>
                              {/*====== WR data 15/09/2026 — vira select com o nome do Grupo (igual
                              à config do projeto) quando a Service Layer devolveu a lista; sem
                              lista carregada, cai pro campo numérico de antes (nunca trava a
                              edição). Pré-preenchido com a sugestão da IA ou o Grupo padrão do
                              projeto (ver handlePrepararComIA) — sempre editável. */}
                              {gruposSap.length ? (
                                <Input
                                  type="select"
                                  bsSize="sm"
                                  style={{ minWidth: 160 }}
                                  value={row.ItemsGroupCode != null ? String(row.ItemsGroupCode) : ""}
                                  onChange={(e) =>
                                    updateImportField(
                                      row,
                                      "ItemsGroupCode",
                                      e.target.value ? Number(e.target.value) : null
                                    )
                                  }
                                >
                                  <option value="">— selecione —</option>
                                  {gruposSap.map((g) => (
                                    <option key={g.code} value={String(g.code)}>
                                      {g.name}
                                    </option>
                                  ))}
                                  {row.ItemsGroupCode != null &&
                                    !gruposSap.some((g) => String(g.code) === String(row.ItemsGroupCode)) && (
                                      <option value={String(row.ItemsGroupCode)}>
                                        Grupo {row.ItemsGroupCode}
                                      </option>
                                    )}
                                </Input>
                              ) : (
                                <Input
                                  bsSize="sm"
                                  type="text"
                                  inputMode="numeric"
                                  style={{ minWidth: 90 }}
                                  value={row.ItemsGroupCode ?? ""}
                                  onChange={(e) =>
                                    updateImportField(
                                      row,
                                      "ItemsGroupCode",
                                      e.target.value ? Number(e.target.value.replace(/\D/g, "")) : null
                                    )
                                  }
                                />
                              )}
                            </td>
                            <td>
                              {unidadesSap.length ? (
                                <Input
                                  type="select"
                                  bsSize="sm"
                                  style={{ minWidth: 120 }}
                                  value={row.InventoryUOM || ""}
                                  onChange={(e) => updateImportField(row, "InventoryUOM", e.target.value)}
                                >
                                  <option value="">— selecione —</option>
                                  {unidadesSap.map((u) => (
                                    <option key={u.code} value={u.code}>
                                      {u.code}
                                      {u.name && u.name !== u.code ? ` (${u.name})` : ""}
                                    </option>
                                  ))}
                                  {row.InventoryUOM &&
                                    !unidadesSap.some((u) => u.code === row.InventoryUOM) && (
                                      <option value={row.InventoryUOM}>{row.InventoryUOM}</option>
                                    )}
                                </Input>
                              ) : (
                                <Input
                                  bsSize="sm"
                                  style={{ minWidth: 90 }}
                                  value={row.InventoryUOM}
                                  onChange={(e) => updateImportField(row, "InventoryUOM", e.target.value)}
                                />
                              )}
                            </td>
                            <td>
                              <Input
                                bsSize="sm"
                                type="select"
                                style={{ minWidth: 170 }}
                                value={row.MaterialType || ""}
                                onChange={(e) => updateImportField(row, "MaterialType", e.target.value || null)}
                              >
                                <option value="">— selecione —</option>
                                {MATERIAL_TYPES.map((mt) => (
                                  <option key={mt.value} value={mt.value}>
                                    {mt.label}
                                  </option>
                                ))}
                              </Input>
                            </td>
                            <td className="small">
                              {row.ncm || "—"}
                              {row.ncm && (
                                <div className={row.ncmValido ? "text-success" : "text-warning"}>
                                  {row.ncmValido ? "válido no SAP" : "não cadastrado no SAP"}
                                </div>
                              )}
                            </td>
                            <td>
                              <Input
                                bsSize="sm"
                                style={{ minWidth: 90 }}
                                placeholder="opcional"
                                value={row.CestCode || ""}
                                onChange={(e) => updateImportField(row, "CestCode", e.target.value)}
                              />
                            </td>
                            <td className="small">
                              {row.origemIcms != null && row.origemIcms !== ""
                                ? ORIGEM_ICMS_LABELS[row.origemIcms] || row.origemIcms
                                : "—"}
                            </td>
                            <td>
                              <Input
                                bsSize="sm"
                                type="select"
                                style={{ minWidth: 110 }}
                                value={row.AdministrarPor || "nenhum"}
                                onChange={(e) => updateImportField(row, "AdministrarPor", e.target.value)}
                              >
                                <option value="nenhum">Nenhum</option>
                                <option value="serie">Nº de série</option>
                                <option value="lote">Lote</option>
                              </Input>
                            </td>
                            <td>
                              <Input
                                bsSize="sm"
                                type="select"
                                style={{ minWidth: 90 }}
                                value={row.WTLiable || "tNO"}
                                onChange={(e) => updateImportField(row, "WTLiable", e.target.value)}
                              >
                                <option value="tYES">Sim</option>
                                <option value="tNO">Não</option>
                              </Input>
                            </td>
                            {["InventoryItem", "SalesItem", "PurchaseItem"].map((campo) => (
                              <td key={campo}>
                                <Input
                                  bsSize="sm"
                                  type="select"
                                  style={{ minWidth: 90 }}
                                  value={row[campo]}
                                  onChange={(e) => updateImportField(row, campo, e.target.value)}
                                >
                                  <option value="tYES">Sim</option>
                                  <option value="tNO">Não</option>
                                </Input>
                              </td>
                            ))}
                            <td className="small">{row.cEAN || "—"}</td>
                            <td>
                              {resultado?.status === "sucesso" && <Badge color="success">Gravado</Badge>}
                              {resultado?.status === "erro" && (
                                <Badge color="danger" title={resultado.mensagem}>
                                  Erro
                                </Badge>
                              )}
                              {!resultado && !validacao.ok && (
                                <Badge color="warning" title={validacao.erros.join(" | ")}>
                                  Inválido
                                </Badge>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
          </TabPane>
        </TabContent>
      </CardBody>
    </Card>
  )
}
